import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

export type FrameIssueCaptionItem = {
  id: string;
  n: number;
  title: string;
  group: "high" | "medium" | "low";
  sevLabel: string;
};

export type FrameIssueEmptyCaption = {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
};

type Props = {
  issues: FrameIssueCaptionItem[];
  showNumber: boolean;
  expanded: boolean;
  hoveredId: string | null;
  openId: string | null;
  emptyCaption?: FrameIssueEmptyCaption;
  onToggle: () => void;
  onHover: (id: string | null) => void;
  onOpen: (id: string, origin: HTMLElement | null) => void;
};

const GROUP_ORDER = ["high", "medium", "low"] as const;

export default function FrameIssueCaption({
  issues,
  showNumber,
  expanded,
  hoveredId,
  openId,
  emptyCaption,
  onToggle,
  onHover,
  onOpen,
}: Props) {
  if (issues.length === 0) {
    if (emptyCaption?.actionLabel && emptyCaption.onAction) {
      return (
        <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-40 bg-gray-900/90 text-white">
          <button
            type="button"
            className="flex h-10 w-full cursor-pointer items-center px-3 text-left font-sans text-[13px] font-semibold leading-none hover:bg-white/5"
            onClick={emptyCaption.onAction}
          >
            {emptyCaption.actionLabel}
          </button>
        </div>
      );
    }

    return (
      <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-40 bg-gray-900/90 text-gray-400">
        <p className="flex h-10 items-center px-3 font-sans text-[13px] font-medium leading-none">
          {emptyCaption?.message ?? "No usability issues identified on this page"}
        </p>
      </div>
    );
  }

  const noun = issues.length === 1 ? "usability issue found on this frame" : "usability issues found on this frame";
  const groups = GROUP_ORDER.map((group) => ({
    group,
    items: issues.filter((issue) => issue.group === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-40 flex min-w-0 flex-col bg-gray-900/90 text-white">
      {expanded ? (
        <div className="flex max-h-28 flex-col gap-2 overflow-y-auto overscroll-contain border-b border-white/10 px-1 pb-2 pt-1 sm:max-h-[148px]">
          {groups.map((entry) => (
            <div key={entry.group} className="flex flex-col gap-0.5">
              <p
                className={`m-0 px-2 pt-0.5 font-sans text-[10px] font-bold uppercase tracking-wider ${groupLabelClass(entry.group)}`}
              >
                {entry.group}
              </p>
              {entry.items.map((issue) => {
                const active = hoveredId === issue.id || openId === issue.id;
                const opened = openId === issue.id;
                const name = showNumber
                  ? `Issue ${issue.n}, ${issue.sevLabel}: ${issue.title}`
                  : `${issue.sevLabel}: ${issue.title}`;

                return (
                  <button
                    key={issue.id}
                    type="button"
                    className={`flex w-full min-w-0 cursor-pointer items-center rounded-md px-2 py-1 text-left ${
                      opened
                        ? "bg-primary/45"
                        : active
                          ? "bg-white/12 shadow-[inset_0_0_0_1px_rgb(255_255_255_/_0.22)]"
                          : ""
                    }`}
                    aria-label={`${name}. View issue`}
                    title={`${name}. View issue`}
                    onMouseEnter={() => onHover(issue.id)}
                    onMouseLeave={() => onHover(null)}
                    onClick={(event) => onOpen(issue.id, event.currentTarget)}
                  >
                    <span className="inline-flex min-w-0 items-baseline gap-2.5">
                      {showNumber ? (
                        <span className="font-sans text-[11px] font-bold text-teal-200">{issue.n}</span>
                      ) : null}
                      <span className="font-sans text-[13px] font-semibold leading-snug">{issue.title}</span>
                    </span>
                    <span
                      className={`ml-2 inline-flex shrink-0 items-center gap-0.5 align-baseline text-[10px] font-semibold leading-tight ${
                        opened ? "text-slate-300" : "text-teal-200"
                      }`}
                    >
                      {opened ? "Close" : "View issue"}
                      <ChevronRightIcon className="h-3 w-3" aria-hidden="true" />
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="flex h-10 w-full min-w-0 cursor-pointer items-center justify-between gap-2 px-3 text-left hover:bg-white/5"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="min-w-0 truncate font-sans text-[13px] font-semibold leading-none">
          {issues.length} {noun}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

function groupLabelClass(group: FrameIssueCaptionItem["group"]) {
  if (group === "high") return "text-red-300";
  if (group === "medium") return "text-amber-300";

  return "text-teal-200";
}
